import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import JsonLd from "@/components/feature/JsonLd";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const BASE = "https://kdove.cn";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const [keyword, setKeyword] = useState("");

  const category = categories.find((item) => item.slug === slug);

  const list = useMemo(() => {
    const base = articles.filter((item) => item.category === slug);
    const key = keyword.trim();
    if (!key) return base;
    return base.filter(
      (item) =>
        item.title.includes(key) ||
        item.excerpt.includes(key) ||
        item.tags.some((tag) => tag.includes(key)),
    );
  }, [slug, keyword]);

  useDocumentMeta({
    title: category
      ? `${category.name}｜选美吃瓜现场盘点 - 吃瓜前排`
      : "栏目不存在 - 吃瓜前排",
    description: category
      ? `${category.description} 吃瓜前排为你整理选美吃瓜现场的${category.name}内容。`
      : "你访问的栏目暂时不存在，返回首页继续围观选美吃瓜现场。",
    keywords: category
      ? `选美吃瓜,${category.name},选美吃瓜现场,选美${category.name}`
      : "选美吃瓜,选美吃瓜现场",
    canonical: `${BASE}/category/${slug}`,
  });

  if (!category) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-content flex-col items-center justify-center px-4 py-20 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-emotion-unhappy-line text-2xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          这个栏目暂时还没开张
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          可能链接输错了，也可能是我们还没来得及整理。先去首页看看本季焦点吧。
        </p>
        <Link
          to="/"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category.name,
      description: category.description,
      url: `${BASE}/category/${category.slug}`,
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        {
          "@type": "ListItem",
          position: 2,
          name: category.name,
          item: `${BASE}/category/${category.slug}`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${category.name}文章列表`,
      itemListElement: list.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `${BASE}/article/${item.id}`,
      })),
    },
  ];

  return (
    <>
      <JsonLd data={structuredData} />

      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={category.cover}
            alt={`${category.name}栏目头图`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40" />
        </div>
        <div className="relative mx-auto w-full max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="max-w-2xl">
            <div className="[&_a]:text-background-300 [&_a:hover]:text-background-50 [&_span]:text-background-400">
              <Breadcrumb
                items={[{ label: "首页", to: "/" }, { label: category.name }]}
              />
            </div>
            <span className="mt-5 flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className={`${category.icon} text-xl`} />
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
              {category.name}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
              {category.description}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
            <nav aria-label="栏目切换" className="flex flex-wrap gap-1">
              {categories.map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    item.slug === category.slug
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="relative w-full lg:w-72">
              <span className="pointer-events-none absolute left-3 top-1/2 inline-flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line" />
              </span>
              <label htmlFor="category-search" className="sr-only">
                在本栏目内搜索
              </label>
              <input
                id="category-search"
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="在本栏目内搜索关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>

          <p className="mt-5 text-xs text-foreground-500">
            共 {list.length} 篇内容
            {keyword.trim() ? ` · 关键词「${keyword.trim()}」` : ""}
          </p>

          {list.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-file-search-line text-xl" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">
                这个关键词下暂时没有内容，换个词试试，或者看看其他栏目。
              </p>
              <button
                type="button"
                onClick={() => setKeyword("")}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                清空关键词
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            逛其他栏目
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {categories
              .filter((item) => item.slug !== category.slug)
              .map((item) => (
                <Link
                  key={item.slug}
                  to={`/category/${item.slug}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-lg`} />
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                      {item.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-foreground-500">
                      {item.count} 篇内容
                    </span>
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </>
  );
}